import { AxiosErrorContext } from '@/fetching/axiosErrorContext';
import { isLocal } from '@/utils/axleEnvironment';
import { datadogRum } from '@datadog/browser-rum';
import { captureException, captureMessage, withScope } from '@sentry/react';
import { ErrorInfo } from 'react';

// `ErrorInfo` fields are optional because errors reported from outside React have no
//  component stack to attach.
type ErrorContext = Partial<ErrorInfo> & {
    isGlobal?: boolean;
    isModulePreloadError?: boolean;
    reloadSkipped?: boolean;
};

export const reportError = (error: unknown, errorContext?: ErrorContext) => {
    if (isLocal) {
        // eslint-disable-next-line no-console
        console.error(error, errorContext);
    }
    captureException(error, { data: errorContext });
    datadogRum.addError(error, errorContext);
};

// Reports a failed network request with a structured, searchable `axios` context to both
//  Sentry and Datadog. Unlike dumping the raw AxiosError (which Sentry renders as
//  `[object Object]`), this attaches the useful fields as a Sentry context card + tags and
//  as searchable Datadog RUM attributes, so we can finally tell which endpoint failed, what
//  timeout fired, how long it ran, and what the backend returned.
export const reportNetworkError = (error: unknown, axiosContext?: AxiosErrorContext) => {
    if (isLocal) {
        // eslint-disable-next-line no-console
        console.error('[network error]', error, axiosContext);
    }
    withScope((scope) => {
        if (axiosContext) {
            // `contexts` renders as a structured card in Sentry; `tags` are indexed and searchable.
            scope.setContext('axios', { ...axiosContext });
            scope.setTag('axios.code', axiosContext.code);
            scope.setTag('axios.status', axiosContext.responseStatus);
        }
        captureException(error);
    });
    datadogRum.addError(error, { axios: axiosContext });
};

export const logMessage = (message: string) => {
    captureMessage(message);
    datadogRum.addAction(message);
};
